import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  Fragment,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Listing, LOCATION_STATE, NZ_STATE } from 'models/Listing';
import EmptyScreen from 'components/EmptyScreen';
import PriceRangeFilter from './components/filters/PriceRangeFilter';
import IndustryFilter from './components/filters/IndustryFilter';
import LocationFilter from './components/filters/LocationFilter';
import TrendingBusinesses from './components/TrendingBusinesses';
import api from 'api';
import utils from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ResolvePagination from 'components/ResolvePagination';
import { Link } from 'react-router-dom';
import ExclamationIcon from 'icons/Exclamation.icon';
import AdvertisingBusinessCard from 'components/AdvertisingBusinessCard';
import EbidtaRangeFilter from './components/filters/EbidtaRangeFilter';
import ArrowrightIcon from 'icons/Arrowright.icon';
import useSiteInitializedState from 'hooks/useSiteInitializedState';
import HelmetWrapper from 'components/HelmetWrapper';
import useHelmetInfo from '../../../hooks/useHelmetInfo';
import NudgeBanner from 'components/NudgeBanner';
import { FILTER_FIELDS, LIMIT } from './data';
import { URGENT_ROUTE } from 'data/global';
import BusinessesTitle from '../../../components/BusinessesTitle';
import BusinessesFilters from '../../../components/BusinessesFilters';
import useSponsoredListings from '../../../hooks/useSponsoredListings';
import useBusinessesCommon from '../../../hooks/useBusinessesCommon';
import useResetPage from '../../../hooks/useResetPage';
import useSearchParams from 'hooks/useSearchParams';
import useBusinessesFilters from '../../../hooks/useBusinessesFilters';
import useBusinesses from './hooks/useBusinesses';
import CorrectTierCard from 'components/CorrectTierCard';
import useListingCardPlaceholder from 'hooks/useListingCardPlaceholder';
import { DraftListingBanner } from 'components/DraftListingBanner';

import './Businesses.styles.scss';
import './Businesses.styles.responsive.scss';
import '../../../BusinessesCommon.styles.scss';
import '../../../BusinessesCommon.styles.responsive.scss';

type BusinessesProps = {
  className?: string;
};

const Businesses: React.FC<BusinessesProps> = (props) => {
  const { className } = props;

  const [loading, setLoading] = useState(utils.isSSGHuman());
  const [totalPages, setTotalPages] = useState(0);

  const [listings, setListings] =
    useSiteInitializedState<Array<Listing>>('listings-array');

  const { popServerError } = useContext(PopNotificationsContext);

  const latestFetchId = useRef(0);

  const classes = classNames('businesses', 'businesses-common', className);

  const { t } = useTranslation();

  const { pageTitle, activeNudge, setActiveNudge } = useBusinesses();

  const { numOfActiveFilter } = useBusinessesFilters();

  const searchParams = useSearchParams();

  const { $page: currentPage } = searchParams;

  const { title, description, url } = useHelmetInfo(currentPage);

  const { sponsoredAdvert, rightRailAd } = useSponsoredListings(currentPage);

  const placeholder = useListingCardPlaceholder(+LIMIT);

  useBusinessesCommon();

  useResetPage(totalPages, currentPage);

  useEffect(() => {
    !utils.isSSGHuman() &&
      (async () => {
        try {
          setLoading(true);
          setListings([]);
          const currentFetchId = +new Date();
          latestFetchId.current = currentFetchId;

          const { location, to, from, ...restParams } = searchParams;

          const locationState = location
            ? utils.replaceElementInStringArray(location, NZ_STATE, [
                LOCATION_STATE.NNZ,
                LOCATION_STATE.SNZ,
              ])
            : location;

          const {
            data: { totalPages, items },
          } = await api.listings.getListings({
            $limit: LIMIT,
            ...restParams,
            locationState,
            priceTo: to,
            priceFrom: from,
          });

          if (latestFetchId.current === currentFetchId) {
            setTotalPages(totalPages);
            setListings(items);
          }
        } catch (e) {
          popServerError(e);
        } finally {
          setLoading(false);
        }
      })();
  }, [searchParams, popServerError, setListings]);

  return (
    <div className={classes}>
      <HelmetWrapper title={title} description={description} url={url} />

      {activeNudge && (
        <NudgeBanner nudge={activeNudge} onClose={() => setActiveNudge(null)} />
      )}

      <BusinessesTitle
        title={pageTitle}
        ariaLabel={t('businessesForSale')}
        currentPage={currentPage}
      />

      <BusinessesFilters fields={FILTER_FIELDS}>
        <PriceRangeFilter />
        <EbidtaRangeFilter />
        <LocationFilter />
        <IndustryFilter />
      </BusinessesFilters>

      <div className="businesses-common__container">
        <div className="businesses-common__listings">
          <div className="businesses-common__listings__container">
            {loading ? (
              placeholder
            ) : !listings?.length ? (
              numOfActiveFilter ? (
                <p className="no-result-filter">
                  <ExclamationIcon />
                  {t('noResultsWithFilters')}
                </p>
              ) : (
                <EmptyScreen
                  title={t('noBusinessFound')}
                  content={t('noBusinessesForSearhcedCriteria')}
                />
              )
            ) : (
              listings.map((listing, index) => {
                const Card = (
                  <CorrectTierCard
                    key={listing._id}
                    listing={listing}
                    updateListing={(listingId, updatedValues) =>
                      setListings((old) =>
                        old.map((el) =>
                          el._id !== listingId
                            ? el
                            : { ...el, ...updatedValues },
                        ),
                      )
                    }
                  />
                );

                if (index === 4 && sponsoredAdvert) {
                  return (
                    <Fragment key={sponsoredAdvert._id}>
                      {Card}
                      <AdvertisingBusinessCard
                        title={sponsoredAdvert.title}
                        description={sponsoredAdvert.description}
                        coverPhoto={sponsoredAdvert.coverPhoto}
                        logoImage={sponsoredAdvert.logoImage}
                        link={sponsoredAdvert.link}
                        ctaText={sponsoredAdvert.ctaText}
                      />
                    </Fragment>
                  );
                }

                return Card;
              })
            )}
          </div>

          <div className="pagination-container">
            <ResolvePagination totalPages={totalPages} />
            <Link to={URGENT_ROUTE}>
              {t('browseUrgentSaleListings')}
              <ArrowrightIcon />
            </Link>
          </div>
        </div>

        <div className="businesses-common__trendings">
          <div className="sticky-part">
            {rightRailAd && (
              <AdvertisingBusinessCard
                title={rightRailAd.title}
                description={rightRailAd.description}
                coverPhoto={rightRailAd.coverPhoto}
                logoImage={rightRailAd.logoImage}
                link={rightRailAd.link}
                ctaText={rightRailAd.ctaText}
                onlyMobileView
              />
            )}

            <TrendingBusinesses />
          </div>
        </div>
      </div>

      {!activeNudge && <DraftListingBanner />}
    </div>
  );
};

export default Businesses;
